import {
  RISK_LEVELS,
  REVIEW_STATES,
  DECISIONS,
  VENDOR_SOURCES,
  FINDING_SEVERITY,
  FINDING_STATUS,
  SECURITY_REVIEW_STATUS,
  RECOMMENDATIONS,
} from "../utils/constants";

// Bump this when the seed shape changes so persisted data re-seeds cleanly.
export const SEED_VERSION = 3;

// Reusable security-review criteria applied when a fresh review is started.
export const SECURITY_CRITERIA_TEMPLATE = [
  {
    key: "soc2",
    name: "Independent Audit",
    requirement: "Maintains a current SOC 2 Type II or ISO 27001 certification.",
  },
  {
    key: "encryption",
    name: "Encryption",
    requirement: "Encrypts customer data in transit (TLS 1.2+) and at rest (AES-256).",
  },
  {
    key: "access",
    name: "Access Control",
    requirement: "Enforces SSO, MFA and least-privilege access for employees.",
  },
  {
    key: "pentest",
    name: "Penetration Testing",
    requirement: "Conducts annual third-party penetration testing and remediates findings.",
  },
  {
    key: "incident",
    name: "Incident Response",
    requirement: "Has a documented incident response plan with breach notification SLAs.",
  },
  {
    key: "subprocessors",
    name: "Subprocessor Management",
    requirement: "Maintains and discloses a list of subprocessors with a DPA in place.",
  },
  {
    key: "bcdr",
    name: "Business Continuity",
    requirement: "Maintains tested business continuity and disaster recovery plans.",
  },
];

// Reusable questionnaire questions sent to vendors / answered internally.
export const SECURITY_QUESTIONNAIRE_TEMPLATE = [
  { key: "q_certs", question: "Which security certifications does your organization currently hold?" },
  { key: "q_data", question: "How is customer data encrypted in transit and at rest?" },
  { key: "q_mfa", question: "Is MFA enforced for all employees with access to customer data?" },
  { key: "q_pentest", question: "When was your most recent third-party penetration test?" },
  { key: "q_breach", question: "Have you experienced a security breach in the last 24 months?" },
  { key: "q_sub", question: "Do you use subprocessors, and is a current list available?" },
];

const daysFromNow = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString();
};

export const buildFreshReview = ({
  id,
  title = "Security Review - 1",
  owner = "",
  createdAt,
  files = [],
} = {}) => ({
  id: id || "r-new",
  title,
  status: SECURITY_REVIEW_STATUS.DRAFT,
  createdAt: createdAt || daysFromNow(0),
  completedAt: null,
  discardedAt: null,
  owner,
  recommendation: null,
  summary: "",
  files,
});

const vendors = [
  {
    id: "v-aws",
    name: "Amazon Web Services",
    logoColor: "#FF9900",
    website: "https://aws.amazon.com",
    category: "Cloud Infrastructure",
    owner: "",
    source: VENDOR_SOURCES.DISCOVERED,
    discoveredFrom: "Google Workspace",
    services: "Primary cloud hosting, compute, storage, and databases",
    contactName: "AWS Enterprise Support",
    contactEmail: "support@aws.amazon.com",
    trustCenterUrl: "https://aws.amazon.com/trust-center/",
    authType: "SSO / SAML",
    contractStart: "2023-04-01",
    contractEnd: "2026-03-31",
    status: "Active",
    reviewState: REVIEW_STATES.COMPLETED,
    lastReviewedAt: daysFromNow(-40),
    nextReviewDate: daysFromNow(325),
    reviewCycleDays: 365,
    riskInputs: {
      dataSensitivity: "regulated",
      dataVolume: "high",
      environmentAccess: "privileged",
      operationalImpact: "critical",
      businessImpact: "severe",
    },
    inherentRisk: RISK_LEVELS.CRITICAL,
    residualRisk: RISK_LEVELS.MEDIUM,
    decision: DECISIONS.APPROVE,
    decisionNotes: "Best-in-class security posture. SOC 2, ISO 27001, FedRAMP all current.",
    reviews: [
      {
        id: "r-aws-0",
        title: "Security Review - 1",
        status: SECURITY_REVIEW_STATUS.DISCARDED,
        createdAt: daysFromNow(-410),
        completedAt: null,
        discardedAt: daysFromNow(-400),
        owner: "",
        recommendation: null,
        summary: "Discarded after the contract renewal started a new review cycle.",
        files: [],
      },
      {
        id: "r-aws-1",
        title: "Security Review - 2",
        status: SECURITY_REVIEW_STATUS.COMPLETED,
        createdAt: daysFromNow(-45),
        completedAt: daysFromNow(-40),
        discardedAt: null,
        owner: "",
        recommendation: RECOMMENDATIONS.APPROVED,
        summary: "Best-in-class security posture. SOC 2, ISO 27001, FedRAMP all current.",
        files: [
          { id: "e-aws-soc2", name: "AWS SOC 2 Type II 2025.pdf", type: "SOC 2 Type II", status: "Valid", uploadedAt: daysFromNow(-45), expiresAt: daysFromNow(320), source: "Trust Center" },
          { id: "e-aws-iso", name: "AWS ISO 27001 Certificate.pdf", type: "ISO 27001", status: "Valid", uploadedAt: daysFromNow(-45), expiresAt: daysFromNow(500), source: "Trust Center" },
        ],
      },
    ],
    evidence: [
      { id: "e-aws-soc2", name: "AWS SOC 2 Type II 2025.pdf", type: "SOC 2 Type II", status: "Valid", uploadedAt: daysFromNow(-45), expiresAt: daysFromNow(320), source: "Trust Center" },
      { id: "e-aws-iso", name: "AWS ISO 27001 Certificate.pdf", type: "ISO 27001", status: "Valid", uploadedAt: daysFromNow(-45), expiresAt: daysFromNow(500), source: "Trust Center" },
    ],
    findings: [],
    activity: [
      { id: "a1", at: daysFromNow(-45), actor: "Vendor owner", action: "Started security review" },
      { id: "a2", at: daysFromNow(-40), actor: "Vendor owner", action: "Recorded decision: Approve" },
    ],
  },
  {
    id: "v-okta",
    name: "Okta",
    logoColor: "#007DC1",
    website: "https://okta.com",
    category: "Identity & Access",
    owner: "",
    source: VENDOR_SOURCES.DISCOVERED,
    discoveredFrom: "Google Workspace",
    services: "Workforce identity, SSO and MFA provider",
    contactName: "Okta Customer Success",
    contactEmail: "success@okta.com",
    trustCenterUrl: "https://trust.okta.com",
    authType: "SSO / SAML",
    contractStart: "2024-01-15",
    contractEnd: "2026-01-14",
    status: "Active",
    reviewState: REVIEW_STATES.OVERDUE,
    lastReviewedAt: daysFromNow(-400),
    nextReviewDate: daysFromNow(-35),
    reviewCycleDays: 365,
    riskInputs: {
      dataSensitivity: "pii",
      dataVolume: "medium",
      environmentAccess: "privileged",
      operationalImpact: "critical",
      businessImpact: "high",
    },
    inherentRisk: RISK_LEVELS.HIGH,
    residualRisk: RISK_LEVELS.MEDIUM,
    decision: DECISIONS.APPROVE_WITH_EXCEPTIONS,
    decisionNotes: "Approved with exception: monitor remediation of 2022 breach follow-ups.",
    reviews: [
      {
        id: "r-okta-1",
        title: "Security Review - 1",
        status: SECURITY_REVIEW_STATUS.COMPLETED,
        createdAt: daysFromNow(-400),
        completedAt: daysFromNow(-365),
        discardedAt: null,
        owner: "",
        recommendation: RECOMMENDATIONS.CONDITIONALLY_APPROVED,
        summary: "Approved with exception: monitor remediation of 2022 breach follow-ups.",
        files: [
          { id: "e-okta-soc2-prev", name: "Okta SOC 2 Type II 2024.pdf", type: "SOC 2 Type II", status: "Valid", uploadedAt: daysFromNow(-400), expiresAt: daysFromNow(-180), source: "Trust Center" },
        ],
      },
      {
        id: "r-okta-2",
        title: "Security Review - 2",
        status: SECURITY_REVIEW_STATUS.DRAFT,
        createdAt: daysFromNow(-18),
        completedAt: null,
        discardedAt: null,
        owner: "",
        recommendation: null,
        summary: "",
        files: [
          { id: "e-okta-soc2", name: "Okta SOC 2 Type II.pdf", type: "SOC 2 Type II", status: "Valid", uploadedAt: daysFromNow(-60), expiresAt: daysFromNow(180), source: "Trust Center" },
          { id: "e-okta-pentest", name: "Okta Pentest Summary 2025.pdf", type: "Penetration Test", status: "Valid", uploadedAt: daysFromNow(-60), expiresAt: daysFromNow(200), source: "Vendor" },
        ],
      },
    ],
    evidence: [
      { id: "e-okta-soc2", name: "Okta SOC 2 Type II.pdf", type: "SOC 2 Type II", status: "Valid", uploadedAt: daysFromNow(-60), expiresAt: daysFromNow(180), source: "Trust Center" },
      { id: "e-okta-pentest", name: "Okta Pentest Summary 2025.pdf", type: "Penetration Test", status: "Valid", uploadedAt: daysFromNow(-60), expiresAt: daysFromNow(200), source: "Vendor" },
    ],
    findings: [
      {
        id: "f-okta-1",
        title: "Incident response SLA needs verification",
        description: "Breach notification SLA not clearly documented following the 2022 support incident.",
        severity: FINDING_SEVERITY.MEDIUM,
        status: FINDING_STATUS.IN_REMEDIATION,
        owner: "",
        remediation: "Request updated IR policy and confirm 72-hour notification commitment.",
        createdAt: daysFromNow(-30),
      },
    ],
    activity: [
      { id: "a1", at: daysFromNow(-60), actor: "Vendor owner", action: "Uploaded SOC 2 report" },
      { id: "a2", at: daysFromNow(-30), actor: "Vendor owner", action: "Logged finding: Incident response SLA" },
    ],
  },
  {
    id: "v-slack",
    name: "Slack",
    logoColor: "#4A154B",
    website: "https://slack.com",
    category: "Communication",
    owner: "",
    source: VENDOR_SOURCES.DISCOVERED,
    discoveredFrom: "Google Workspace",
    services: "Team messaging and collaboration",
    contactName: "Slack Support",
    contactEmail: "feedback@slack.com",
    trustCenterUrl: "https://slack.com/trust",
    authType: "SSO / SAML",
    contractStart: "2023-09-01",
    contractEnd: "2025-08-31",
    status: "Active",
    reviewState: REVIEW_STATES.DUE_SOON,
    lastReviewedAt: daysFromNow(-350),
    nextReviewDate: daysFromNow(12),
    reviewCycleDays: 365,
    riskInputs: {
      dataSensitivity: "internal",
      dataVolume: "medium",
      environmentAccess: "limited",
      operationalImpact: "high",
      businessImpact: "medium",
    },
    inherentRisk: RISK_LEVELS.MEDIUM,
    residualRisk: RISK_LEVELS.LOW,
    decision: DECISIONS.APPROVE,
    decisionNotes: "Standard SaaS collaboration risk. No sensitive data stored by policy.",
    reviews: [
      {
        id: "r-slack-1",
        title: "Security Review - 1",
        status: SECURITY_REVIEW_STATUS.COMPLETED,
        createdAt: daysFromNow(-360),
        completedAt: daysFromNow(-350),
        discardedAt: null,
        owner: "",
        recommendation: RECOMMENDATIONS.APPROVED,
        summary: "Standard SaaS collaboration risk. No sensitive data stored by policy.",
        files: [
          { id: "e-slack-soc2", name: "Slack SOC 2 Type II.pdf", type: "SOC 2 Type II", status: "Expired", uploadedAt: daysFromNow(-370), expiresAt: daysFromNow(-5), source: "Trust Center" },
        ],
      },
    ],
    evidence: [
      { id: "e-slack-soc2", name: "Slack SOC 2 Type II.pdf", type: "SOC 2 Type II", status: "Expired", uploadedAt: daysFromNow(-370), expiresAt: daysFromNow(-5), source: "Trust Center" },
    ],
    findings: [],
    activity: [
      { id: "a1", at: daysFromNow(-350), actor: "Vendor owner", action: "Completed annual review" },
    ],
  },
  {
    id: "v-datadog",
    name: "Datadog",
    logoColor: "#632CA6",
    website: "https://datadoghq.com",
    category: "Analytics",
    owner: "",
    source: VENDOR_SOURCES.MANUAL,
    services: "Infrastructure monitoring and observability",
    contactName: "Datadog Support",
    contactEmail: "support@datadoghq.com",
    trustCenterUrl: "https://trust.datadoghq.com/",
    authType: "SSO / SAML",
    contractStart: "2024-06-01",
    contractEnd: "2026-05-31",
    status: "Active",
    reviewState: REVIEW_STATES.DUE,
    lastReviewedAt: null,
    nextReviewDate: daysFromNow(2),
    reviewCycleDays: 365,
    riskInputs: {
      dataSensitivity: "internal",
      dataVolume: "high",
      environmentAccess: "standard",
      operationalImpact: "high",
      businessImpact: "medium",
    },
    inherentRisk: RISK_LEVELS.HIGH,
    residualRisk: RISK_LEVELS.UNSCORED,
    decision: null,
    decisionNotes: "",
    reviews: [
      {
        id: "r-dd-1",
        title: "Security Review - 1",
        status: SECURITY_REVIEW_STATUS.DRAFT,
        createdAt: daysFromNow(-18),
        completedAt: null,
        discardedAt: null,
        owner: "",
        recommendation: null,
        summary: "",
        files: [
          { id: "e-dd-soc2", name: "Datadog SOC 2 Type II.pdf", type: "SOC 2 Type II", status: "Valid", uploadedAt: daysFromNow(-20), expiresAt: daysFromNow(300), source: "Vendor" },
          { id: "e-dd-iso", name: "Datadog ISO 27001.pdf", type: "ISO 27001", status: "Valid", uploadedAt: daysFromNow(-20), expiresAt: daysFromNow(400), source: "Vendor" },
          { id: "e-dd-pentest", name: "Datadog Pentest Summary.pdf", type: "Penetration Test", status: "Valid", uploadedAt: daysFromNow(-20), expiresAt: daysFromNow(250), source: "Vendor" },
        ],
      },
    ],
    evidence: [
      { id: "e-dd-soc2", name: "Datadog SOC 2 Type II.pdf", type: "SOC 2 Type II", status: "Valid", uploadedAt: daysFromNow(-20), expiresAt: daysFromNow(300), source: "Vendor" },
      { id: "e-dd-iso", name: "Datadog ISO 27001.pdf", type: "ISO 27001", status: "Valid", uploadedAt: daysFromNow(-20), expiresAt: daysFromNow(400), source: "Vendor" },
      { id: "e-dd-pentest", name: "Datadog Pentest Summary.pdf", type: "Penetration Test", status: "Valid", uploadedAt: daysFromNow(-20), expiresAt: daysFromNow(250), source: "Vendor" },
    ],
    findings: [],
    activity: [
      { id: "a1", at: daysFromNow(-20), actor: "Vendor owner", action: "Added vendor manually" },
      { id: "a2", at: daysFromNow(-18), actor: "Vendor owner", action: "Requested evidence" },
    ],
  },
  {
    id: "v-stripe",
    name: "Stripe",
    logoColor: "#635BFF",
    website: "https://stripe.com",
    category: "Payments",
    owner: "",
    source: VENDOR_SOURCES.MANUAL,
    services: "Payment processing and billing",
    contactName: "Stripe Support",
    contactEmail: "support@stripe.com",
    trustCenterUrl: "https://docs.stripe.com/security",
    authType: "API Key",
    contractStart: "2023-02-01",
    contractEnd: "2026-01-31",
    status: "Active",
    reviewState: REVIEW_STATES.COMPLETED,
    lastReviewedAt: daysFromNow(-90),
    nextReviewDate: daysFromNow(275),
    reviewCycleDays: 365,
    riskInputs: {
      dataSensitivity: "regulated",
      dataVolume: "high",
      environmentAccess: "standard",
      operationalImpact: "critical",
      businessImpact: "severe",
    },
    inherentRisk: RISK_LEVELS.CRITICAL,
    residualRisk: RISK_LEVELS.LOW,
    decision: DECISIONS.APPROVE,
    decisionNotes: "PCI DSS Level 1 certified. Handles all cardholder data; we remain out of scope.",
    reviews: [
      {
        id: "r-stripe-1",
        title: "Security Review - 1",
        status: SECURITY_REVIEW_STATUS.COMPLETED,
        createdAt: daysFromNow(-95),
        completedAt: daysFromNow(-90),
        discardedAt: null,
        owner: "",
        recommendation: RECOMMENDATIONS.APPROVED,
        summary: "PCI DSS Level 1 certified. Handles all cardholder data; we remain out of scope.",
        files: [
          { id: "e-stripe-soc2", name: "Stripe SOC 2 Type II.pdf", type: "SOC 2 Type II", status: "Valid", uploadedAt: daysFromNow(-95), expiresAt: daysFromNow(270), source: "Trust Center" },
          { id: "e-stripe-pci", name: "Stripe PCI DSS AoC.pdf", type: "Other", status: "Valid", uploadedAt: daysFromNow(-95), expiresAt: daysFromNow(270), source: "Trust Center" },
        ],
      },
    ],
    evidence: [
      { id: "e-stripe-soc2", name: "Stripe SOC 2 Type II.pdf", type: "SOC 2 Type II", status: "Valid", uploadedAt: daysFromNow(-95), expiresAt: daysFromNow(270), source: "Trust Center" },
      { id: "e-stripe-pci", name: "Stripe PCI DSS AoC.pdf", type: "Other", status: "Valid", uploadedAt: daysFromNow(-95), expiresAt: daysFromNow(270), source: "Trust Center" },
    ],
    findings: [],
    activity: [
      { id: "a1", at: daysFromNow(-95), actor: "Vendor owner", action: "Completed security review" },
      { id: "a2", at: daysFromNow(-90), actor: "Vendor owner", action: "Recorded decision: Approve" },
    ],
  },
  {
    id: "v-notion",
    name: "Notion",
    logoColor: "#000000",
    website: "https://notion.so",
    category: "Productivity",
    owner: "",
    source: VENDOR_SOURCES.DISCOVERED,
    discoveredFrom: "Google Workspace",
    services: "Docs, wiki and knowledge base",
    contactName: "Notion Support",
    contactEmail: "team@makenotion.com",
    trustCenterUrl: "https://trust.notion.com/",
    authType: "OAuth",
    contractStart: "2024-03-01",
    contractEnd: "2026-02-28",
    status: "Active",
    reviewState: REVIEW_STATES.UPCOMING,
    lastReviewedAt: daysFromNow(-120),
    nextReviewDate: daysFromNow(90),
    reviewCycleDays: 365,
    riskInputs: {
      dataSensitivity: "internal",
      dataVolume: "medium",
      environmentAccess: "limited",
      operationalImpact: "medium",
      businessImpact: "low",
    },
    inherentRisk: RISK_LEVELS.MEDIUM,
    residualRisk: RISK_LEVELS.LOW,
    decision: DECISIONS.APPROVE,
    decisionNotes: "Low sensitivity usage. SOC 2 verified.",
    reviews: [
      {
        id: "r-notion-1",
        title: "Security Review - 1",
        status: SECURITY_REVIEW_STATUS.COMPLETED,
        createdAt: daysFromNow(-125),
        completedAt: daysFromNow(-120),
        discardedAt: null,
        owner: "",
        recommendation: RECOMMENDATIONS.APPROVED,
        summary: "Low sensitivity usage. SOC 2 verified.",
        files: [
          { id: "e-notion-soc2", name: "Notion SOC 2 Type II.pdf", type: "SOC 2 Type II", status: "Valid", uploadedAt: daysFromNow(-125), expiresAt: daysFromNow(240), source: "Trust Center" },
        ],
      },
    ],
    evidence: [
      { id: "e-notion-soc2", name: "Notion SOC 2 Type II.pdf", type: "SOC 2 Type II", status: "Valid", uploadedAt: daysFromNow(-125), expiresAt: daysFromNow(240), source: "Trust Center" },
    ],
    findings: [],
    activity: [
      { id: "a1", at: daysFromNow(-120), actor: "Vendor owner", action: "Completed security review" },
    ],
  },
  {
    id: "v-zoom",
    name: "Zoom (legacy)",
    logoColor: "#2D8CFF",
    website: "https://zoom.us",
    category: "Communication",
    owner: "",
    source: VENDOR_SOURCES.MANUAL,
    services: "Video conferencing (replaced by Google Meet)",
    contactName: "Zoom Support",
    contactEmail: "support@zoom.us",
    trustCenterUrl: "https://www.zoom.com/en/trust/",
    authType: "SSO / SAML",
    contractStart: "2022-01-01",
    contractEnd: "2024-12-31",
    status: "Archived",
    reviewState: REVIEW_STATES.COMPLETED,
    lastReviewedAt: daysFromNow(-200),
    nextReviewDate: null,
    reviewCycleDays: 365,
    riskInputs: {
      dataSensitivity: "internal",
      dataVolume: "low",
      environmentAccess: "limited",
      operationalImpact: "low",
      businessImpact: "low",
    },
    inherentRisk: RISK_LEVELS.LOW,
    residualRisk: RISK_LEVELS.LOW,
    decision: DECISIONS.APPROVE,
    decisionNotes: "Offboarded - contract not renewed.",
    reviews: [
      {
        id: "r-zoom-1",
        title: "Security Review - 1",
        status: SECURITY_REVIEW_STATUS.DISCARDED,
        createdAt: daysFromNow(-210),
        completedAt: null,
        discardedAt: daysFromNow(-200),
        owner: "",
        recommendation: null,
        summary: "Review discarded when the vendor was offboarded and the contract was not renewed.",
        files: [],
      },
    ],
    evidence: [],
    findings: [],
    activity: [
      { id: "a1", at: daysFromNow(-200), actor: "Vendor owner", action: "Archived vendor" },
    ],
  },
];

// Vendors auto-detected from connected integrations, pending triage.
const discovered = [
  {
    id: "d-github",
    name: "GitHub",
    logoColor: "#181717",
    category: "Security",
    discoveredFrom: "Google Workspace",
    detectedAt: daysFromNow(-3),
    users: 48,
    website: "https://github.com",
    trustCenterUrl: "https://github.com/security",
    suggestedRisk: RISK_LEVELS.HIGH,
  },
  {
    id: "d-figma",
    name: "Figma",
    logoColor: "#F24E1E",
    category: "Productivity",
    discoveredFrom: "Google Workspace",
    detectedAt: daysFromNow(-3),
    users: 22,
    website: "https://figma.com",
    trustCenterUrl: "https://www.figma.com/security",
    suggestedRisk: RISK_LEVELS.MEDIUM,
  },
  {
    id: "d-hubspot",
    name: "HubSpot",
    logoColor: "#FF7A59",
    category: "Marketing",
    discoveredFrom: "Google Workspace",
    detectedAt: daysFromNow(-6),
    users: 15,
    website: "https://hubspot.com",
    trustCenterUrl: "https://trust.hubspot.com/",
    suggestedRisk: RISK_LEVELS.MEDIUM,
  },
  {
    id: "d-openai",
    name: "OpenAI",
    logoColor: "#10A37F",
    category: "AI / ML",
    discoveredFrom: "Google Workspace",
    detectedAt: daysFromNow(-1),
    users: 31,
    website: "https://openai.com",
    trustCenterUrl: "https://trust.openai.com/",
    suggestedRisk: RISK_LEVELS.HIGH,
  },
];

// Continuous-monitoring alerts surfaced across the vendor portfolio.
const monitoring = [
  {
    id: "m1",
    vendorId: "v-okta",
    vendorName: "Okta",
    severity: "High",
    type: "Vulnerability",
    title: "New CVE affecting Okta Verify disclosed",
    description: "CVE-2025-1234 (CVSS 8.1) affects older Okta Verify clients. Confirm patched versions.",
    date: daysFromNow(-2),
    acknowledged: false,
  },
  {
    id: "m2",
    vendorId: "v-slack",
    vendorName: "Slack",
    severity: "Medium",
    type: "Posture Change",
    title: "SOC 2 report expired",
    description: "Slack's SOC 2 Type II on file has expired. Request the latest report.",
    date: daysFromNow(-5),
    acknowledged: false,
  },
  {
    id: "m3",
    vendorId: "v-aws",
    vendorName: "Amazon Web Services",
    severity: "Info",
    type: "Certification",
    title: "AWS published updated ISO 27001 certificate",
    description: "A refreshed ISO 27001 certificate is available on the AWS Trust Center.",
    date: daysFromNow(-8),
    acknowledged: true,
  },
  {
    id: "m4",
    vendorId: "v-datadog",
    vendorName: "Datadog",
    severity: "Medium",
    type: "News",
    title: "Datadog reported a brief service degradation",
    description: "A multi-hour degradation was reported in the US1 region. No data loss disclosed.",
    date: daysFromNow(-10),
    acknowledged: false,
  },
  {
    id: "m5",
    vendorId: "v-stripe",
    vendorName: "Stripe",
    severity: "Info",
    type: "Certification",
    title: "Stripe renewed PCI DSS Level 1 attestation",
    description: "Stripe's PCI DSS Level 1 AoC has been renewed for the current period.",
    date: daysFromNow(-14),
    acknowledged: true,
  },
];

const vendorSeedData = { vendors, discovered, monitoring };

export default vendorSeedData;
